import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { PageHead, Skeleton, ErrorState, EmptyState } from "@/components/Ui";
import { Reveal } from "@/components/Reveal";
import { ChevronDown } from "lucide-react";

export default function Faq() {
  const { t, lang } = useLang();
  const [state, setState] = useState({ items: null, loading: true, error: null });
  const [open, setOpen] = useState(null);

  const load = () => {
    setState({ items: null, loading: true, error: null });
    apiGet("/faq")
      .then((items) => setState({ items, loading: false, error: null }))
      .catch((error) => setState({ items: null, loading: false, error }));
  };
  useEffect(load, []);

  const { items, loading, error } = state;

  return (
    <div className="bg-grid" data-testid="faq-page">
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <PageHead eyebrow={t("faq.eyebrow")} title={t("faq.title")} sub={t("faq.sub")} testid="faq-head" />
        <div className="mt-12">
          {loading ? (
            <div className="space-y-4" data-testid="faq-loading">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : error ? (
            <ErrorState onRetry={load} />
          ) : !items || items.length === 0 ? (
            <EmptyState message={t("faq.empty")} testid="faq-empty" />
          ) : (
            <div className="space-y-4" data-testid="faq-accordion">
              {items.map((item, i) => {
                const isOpen = open === item.id;
                return (
                  <Reveal key={item.id} delay={i * 60}>
                    <div className="mg-card overflow-hidden" data-testid={`faq-item-${i}`}>
                      <button
                        onClick={() => setOpen(isOpen ? null : item.id)}
                        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                        aria-expanded={isOpen}
                        data-testid={`faq-question-${i}`}
                      >
                        <span className="font-display text-base font-bold text-marine">
                          {lang === "fr" ? item.q_fr : item.q_en}
                        </span>
                        <ChevronDown className={`h-5 w-5 shrink-0 text-gold transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                      </button>
                      <div
                        className={`grid transition-[grid-template-rows] duration-300 ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
                      >
                        <div className="overflow-hidden">
                          <p className="px-6 pb-5 text-sm leading-relaxed text-slate-600" data-testid={`faq-answer-${i}`}>
                            {lang === "fr" ? item.a_fr : item.a_en}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
        <Reveal className="mt-12 text-center">
          <p className="text-sm text-slate-500">{t("faq.stillQuestions")}</p>
          <Link to="/contact" className="btn-primary mt-4" data-testid="faq-contact-button">
            {t("faq.contactUs")}
          </Link>
        </Reveal>
      </div>
    </div>
  );
}
